
import { Pipe, PipeTransform } from '@angular/core';
import { DatePipe, DecimalPipe, CurrencyPipe } from '@angular/common';

// Get AppFormats from localStorage, fallback to defaults if not set
const DEFAULT_FORMATS = {
  date: 'MMM d, yyyy',
  date_with_time: 'MMM d, y h:mm a',
  currency: '1.2-2',
  number: '1.0-0',
  float: '1.2-2',
  locale: 'en-US',
  currency_symbol: 'USD'
};
const AppFormats = (() => {
  try {
    return JSON.parse(localStorage.getItem('appConfig') ?? '') || DEFAULT_FORMATS;
  } catch {
    return DEFAULT_FORMATS;
  }
})();
const LOCALE = AppFormats.locale || 'en-US';

export class Formatter {
  private static datePipe = new DatePipe(LOCALE);
  private static decimalPipe = new DecimalPipe(LOCALE);
  private static currencyPipe = new CurrencyPipe(LOCALE);

  // If an error occurs, return the original value unchanged to avoid breaking UI
  static formatDate(value: Date | string | number, withTime: boolean = false): any {
    try {
      const format = withTime ? AppFormats.date_with_time : AppFormats.date;
      const res = Formatter.datePipe.transform(value, format, undefined, LOCALE);
      return res != null ? res : value;
    } catch (e) {
      return value;
    }
  }

  static formatNumber(value: number, isFloat: boolean = false): any {
    try {
      const pattern = isFloat ? AppFormats.float : AppFormats.number;
      const res = Formatter.decimalPipe.transform(value, pattern, LOCALE);
      return res != null ? res : value;
    } catch (e) {
      return value;
    }
  }

  static formatCurrency(value: number): any {
    try {
      const pattern = AppFormats.currency;
      const code = AppFormats.currency_symbol || 'USD';
      const res = Formatter.currencyPipe.transform(value, code, 'symbol', pattern, LOCALE);
      return res != null ? res : value;
    } catch (e) {
      return value;
    }
  }

  static getDateFormat(value: string): any {
    try {
      return AppFormats.date || 'MMM d, yyyy';
    } catch (e) {
      return value;
    }
  }

  // Generic format function
  static format(value: any, type: 'date' | 'date_with_time' | 'number' | 'float' | 'currency' | 'date_format'): any {
    try {
      switch (type) {
        case 'date':
          return Formatter.formatDate(value, false);
        case 'date_with_time':
          return Formatter.formatDate(value, true);
        case 'number':
          return Formatter.formatNumber(value, false);
        case 'float':
          return Formatter.formatNumber(value, true);
        case 'currency':
          return Formatter.formatCurrency(value);
        case 'date_format':
          return Formatter.getDateFormat(value);
        default:
          return value;
      }
    } catch (e) {
      return value;
    }
  }
}

@Pipe({
  name: 'appFormat',
  pure: true
})
export class AppFormatPipe implements PipeTransform {
  /**
   * Usage:
   *   {{ value | appFormat:type:currencyCode }}
   *   type: 'date', 'date_with_time', 'number', 'float', 'currency'
   *   currencyCode: only for currency
   */
  transform(
    value: any,
    type: 'date' | 'date_with_time' | 'number' | 'float' | 'currency' | 'date_format'
  ): string | null {
    if (value == null || value === '' || value === undefined) {
      return '-';
    }
    return Formatter.format(value, type);
  }
}

/**
 * Return a localized currency symbol for a currency code or symbol.
 * If currencyCode is a 3-letter ISO code (e.g. 'USD') this attempts to
 * retrieve the locale-aware symbol via Intl.NumberFormat; otherwise it
 * returns the provided value or the appFormat.currency_symbol fallback.
 */
export function getCurrencySymbol(currencyCode?: string, locale?: string): string {
  const cfg = (() => {
    try { return JSON.parse(localStorage.getItem('appConfig') ?? '') || {}; } catch { return {}; }
  })();
  const fallback = cfg.currency_symbol || 'USD';
  const code = currencyCode || fallback;
  if (!code) return '';
  // If code looks like a 3-letter currency code, try Intl
  if (/^[A-Za-z]{3}$/.test(code)) {
    try {
      const loc = locale || cfg.locale || 'en-US';
      const parts = new Intl.NumberFormat(loc, { style: 'currency', currency: code, minimumFractionDigits: 0 }).formatToParts(0);
      const sym = parts.find(p => p.type === 'currency');
      if (sym && sym.value) return sym.value;
    } catch {
      // fall through
      
    }
  }
  return code;
}

@Pipe({ name: 'currencySymbol', pure: true })
export class CurrencySymbolPipe implements PipeTransform {
  transform(currencyCode?: string, locale?: string): string {
    return getCurrencySymbol(currencyCode, locale);
  }
}